import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import LoginIcon from '@mui/icons-material/Login';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DoughnutGraph, GroupedBarGraph } from './Graphs';


// CustomCard overall mini cards
export const CustomCard = ({ metric, gridProps }) => {
  const IconComp = metric.icon;
  const val = metric.value || 0;
  const lastVal = metric.lastValue || 0;
  const total = metric.total;
  
  const hasComparison = metric.lastValue !== undefined && metric.total !== undefined;
  
  const diff = val - lastVal;
  let percent = "0";
  if (lastVal > 0) {
    percent = Math.abs(Math.round((diff / lastVal) * 100));
  } else if (val > 0) {
    percent = "100";
  }

  // Display value: for Overall Statistics, the big number is total (e.g. 20, 86, 0, 2)
  const displayValue = total !== undefined ? total : val;

  return (
    <Grid item xs={12} sm={6} md={3} {...(metric.gridProps || gridProps)}>
      <Link to={metric.link || "#"} style={{ textDecoration: "none", display: "block", height: "100%" }}>
        <Paper
          id={metric.id}
          elevation={0}
          sx={{
            p: '16px 18px',
            borderRadius: '14px',
            bgcolor: metric.cardBg || '#f8fafc',
            border: '1px solid rgba(226, 232, 240, 0.95)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: '#ffffff',
              boxShadow: '0 8px 20px -4px rgba(0, 0, 0, 0.06)',
              transform: 'translateY(-2px)',
              borderColor: 'rgba(203, 213, 225, 0.95)',
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            textAlign: 'left',
          }}
        >
          {/* Top Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8, textAlign: 'left' }}>
            {IconComp && (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: metric.bg || 'rgba(37, 99, 235, 0.08)',
                  flexShrink: 0,
                }}
              >
                <IconComp sx={{ color: metric.color || '#2563eb', fontSize: 24 }} />
              </Box>
            )}

            <Box sx={{ minWidth: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  lineHeight: 1.25,
                  textAlign: 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {metric.label}
              </Typography>

              {metric.userName && (
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    mt: 0.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metric.userName}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 0.4, textAlign: 'left' }}>
                <Typography
                  sx={{
                    fontSize: '1.85rem',
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1,
                    textAlign: 'left',
                  }}
                >
                  {displayValue}
                </Typography>

                {hasComparison && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px',
                      px: '8px',
                      py: '2px',
                      borderRadius: '20px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      bgcolor: diff > 0 ? '#dcfce7' : diff < 0 ? '#fee2e2' : '#f1f5f9',
                      color: diff > 0 ? '#15803d' : diff < 0 ? '#b91c1c' : '#334155',
                    }}
                  >
                    {diff > 0 ? `↑ ${percent}%` : diff < 0 ? `↓ ${percent}%` : `• 0%`}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Bottom Row / Comparison */}
          {hasComparison ? (
            <Box sx={{ mt: 1.6, pt: 1, borderTop: '1px solid rgba(241, 245, 249, 1)', textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  color: '#475569',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                This Month: <span style={{ fontWeight: 800, color: '#0f172a' }}>{val}</span>
                {"  |  "}
                Last Month: <span style={{ fontWeight: 800, color: '#0f172a' }}>{lastVal}</span>
              </Typography>
            </Box>
          ) : metric.valueLabel ? (
            <Box sx={{ mt: 1, textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  color: '#475569',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                {metric.valueLabel}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </Link>
    </Grid>
  );
};

// SectionHeader component 
export const SectionHeader = ({ title, action, mt = 5 }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ height: 24, width: 4, bgcolor: "primary.main", borderRadius: "999px" }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
    </Box>
    {action && <Box>{action}</Box>}
  </Box>
);

// graphical cards for all 3 graphs
export const SummarySection = ({ 
  controls, 
  chart, 
  stats, 
  summaryTitle, 
  footerNote, 
  footerIconColor = "warning.main",
  chartColSize = 5,
  statsColSize = 7,
  paperSx = { p: 3, borderRadius: 2, boxShadow: 1, borderColor: "divider" }
}) => {
  return (
    <>
      <Box>
        <Paper variant="outlined" sx={paperSx}>
          {controls && <Box sx={{ mb: 3, display: "flex", justifyContent: "left" }}>{controls}</Box>}
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={chartColSize}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
                {chart}
              </Box>
            </Grid>
            <Grid item xs={12} md={statsColSize}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}>
                  {summaryTitle}
                </Typography>

                <Grid container spacing={2}>
                  {stats.map((stat, i) => (
                    <Grid item xs={6} key={i}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: 2, bgcolor: stat.bg }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {stat.label}
                          </Typography>
                          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: stat.color, lineHeight: 1.2, mt: 0.3 }}>
                            {stat.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {footerNote && (
                  <Box sx={{ mt: 1, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography component="div" variant="body2" sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 4, height: 16, bgcolor: footerIconColor, borderRadius: 1 }} />
                      {footerNote}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

// Generic widget container card with subtle border and elevation
export const DashboardWidgetCard = ({ children, sx = {}, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      p: '20px 22px',
      borderRadius: '16px',
      bgcolor: '#ffffff',
      border: '1px solid rgba(226, 232, 240, 0.85)',
      boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'left',
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

// Shared Widget Header
export const WidgetHeader = ({ title, action, mb = 1.5, titleSx = {} }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb }}>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', ...titleSx }}>
      {title}
    </Typography>
    {action && <Box>{action}</Box>}
  </Box>
);

// Shared Timeframe Dropdown Select
export const WidgetTimeframeSelect = ({
  value,
  onChange,
  minWidth = 110,
  options = [
    { value: 'today', label: 'Today' },
    { value: 'monthly', label: 'This Month' },
    { value: 'overall', label: 'Overall' },
  ],
}) => (
  <Select
    value={value}
    onChange={(e) => onChange && onChange(e.target.value)}
    size="small"
    sx={{
      minWidth,
      height: 30,
      fontSize: '0.78rem',
      bgcolor: '#f8fafc',
      borderRadius: '8px',
      borderColor: '#e2e8f0',
      fontWeight: 600,
      '& .MuiSelect-select': { py: '3px', px: '10px' },
    }}
  >
    {options.map((opt) => (
      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.78rem' }}>
        {opt.label}
      </MenuItem>
    ))}
  </Select>
);

// Row 3 - Left: Work Report Widget (Vertical List like Today's Activity)
export const WorkReportWidget = ({
  performanceData = { topUser: {}, minUser: {} },
  approverPerformanceData = { topUser: {}, minUser: {} },
  timeframe = "today",
  onTimeframeChange,
}) => {
  const reportItems = [
    {
      label: "Top Calibrator",
      userName: performanceData?.topUser?.name || "N/A",
      value: performanceData?.topUser?.count || 0,
      color: "#16a34a",
      bg: "rgba(22, 163, 74, 0.08)",
      icon: EmojiEventsIcon,
    },
    {
      label: "Lowest Calibrator",
      userName: performanceData?.minUser?.name || "N/A",
      value: performanceData?.minUser?.count || 0,
      color: "#ea580c",
      bg: "rgba(234, 88, 12, 0.08)",
      icon: GroupIcon,
    },
    {
      label: "Top Approver",
      userName: approverPerformanceData?.topUser?.name || "N/A",
      value: approverPerformanceData?.topUser?.count || 0,
      color: "#2563eb",
      bg: "rgba(37, 99, 235, 0.08)",
      icon: WorkspacePremiumIcon,
    },
    {
      label: "Lowest Approver",
      userName: approverPerformanceData?.minUser?.name || "N/A",
      value: approverPerformanceData?.minUser?.count || 0,
      color: "#9333ea",
      bg: "rgba(147, 51, 234, 0.08)",
      icon: GroupIcon,
    },
  ];

  return (
    <DashboardWidgetCard>
      <WidgetHeader
        title="Work Report"
        mb={1.8}
        action={<WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />}
      />

      {/* Activity List - Clean rows matching Today's Activity */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, flexGrow: 1, justifyContent: 'space-around' }}>
        {reportItems.map((item, idx) => {
          const IconComp = item.icon;
          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: '7px',
                px: '8px',
                borderRadius: '8px',
                transition: 'background-color 150ms ease',
                '&:hover': {
                  bgcolor: '#f8fafc',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    bgcolor: item.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComp sx={{ color: item.color, fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 650, color: '#1e293b', lineHeight: 1.2 }}>
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.74rem',
                      fontWeight: 500,
                      color: '#64748b',
                      mt: 0.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.userName}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.94rem',
                    fontWeight: 750,
                    color: item.color,
                  }}
                >
                  {item.value || 0}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 500, color: '#94a3b8' }}>
                  jobs
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 3 - Right: Refined Projects Statistics inside DashboardWidgetCard
export const ProjectStatistics = ({ 
  title = "Project Statistics", 
  subtitle = "Overview of your calibration and certificate progress",
  stats = {}, 
  timeframe = "today", 
  onTimeframeChange,
}) => {
  const checkpoints = [
    {
      label: "INWARD",
      value: stats.inward || 0,
      iconComponent: <LoginIcon sx={{ color: '#2563eb', fontSize: 20 }} />,
    },
    {
      label: "TOTAL INSTRUMENTS",
      value: stats.totalInstruments || 0,
      iconComponent: <ViewInArIcon sx={{ color: '#2563eb', fontSize: 20 }} />,
    },
    {
      label: "CALIBRATED",
      value: stats.calibrated || 0,
      iconComponent: <SettingsOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />,
    },
    {
      label: "PENDING CALIBRATION",
      value: stats.pendingCalibration || 0,
      iconComponent: <AccessTimeOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />,
    },
    {
      label: "APPROVED CERTIFICATES",
      value: stats.approvedCertificates || 0,
      iconComponent: <CardMembershipOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />,
    },
    {
      label: "PENDING APPROVE",
      value: stats.pendingApproved || 0,
      iconComponent: <PendingActionsOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />,
    },
    {
      label: "REJECTED CERTIFICATES",
      value: stats.rejectedCertificates || 0,
      iconComponent: (
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArticleOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
          <CancelIcon
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -3,
              fontSize: 10,
              color: '#2563eb',
              bgcolor: '#eff6ff',
              borderRadius: '50%',
            }}
          />
        </Box>
      ),
    },
    {
      label: "AMENDMENT CERTIFICATES",
      value: stats.amendmentCertificates || 0,
      iconComponent: (
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArticleOutlinedIcon sx={{ color: '#2563eb', fontSize: 20 }} />
          <EditIcon
            sx={{
              position: 'absolute',
              bottom: -2,
              right: -3,
              fontSize: 10,
              color: '#2563eb',
              bgcolor: '#eff6ff',
              borderRadius: '50%',
            }}
          />
        </Box>
      ),
    },
  ];

  return (
    <DashboardWidgetCard sx={{ p: { xs: '14px 12px', sm: '16px 18px', md: '16px 20px' } }}>
      {/* Header with Icon, Title, Subtitle and Timeframe Select */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3, textAlign: 'left' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '9px',
              bgcolor: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <BarChartIcon sx={{ color: '#2563eb', fontSize: 20 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
            <Typography sx={{ fontSize: '0.96rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2, textAlign: 'left' }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: '0.73rem', color: '#64748b', fontWeight: 450, mt: 0.2, textAlign: 'left' }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />
      </Box>

      {/* 8 Metric Cards Grid - Centered in middle with compact height */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(4, 1fr)',
            md: 'repeat(8, minmax(0, 1fr))',
          },
          gap: { xs: 0.8, sm: 1, md: 1 },
          width: '100%',
          my: 'auto',
          py: { xs: 1, md: 1.5 },
        }}
      >
        {checkpoints.map((step, idx) => (
          <Box
            key={idx}
            sx={{
              bgcolor: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              py: { xs: 1.4, sm: 1.6, md: 1.8 },
              px: { xs: 0.4, sm: 0.6 },
              minHeight: { xs: 110, sm: 120, md: 125 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
            }}
          >
            {/* Soft Blue Icon Container */}
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '9px',
                bgcolor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.9,
                flexShrink: 0,
              }}
            >
              {step.iconComponent}
            </Box>

            {/* Big Value Number */}
            <Typography
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.38rem', md: '1.45rem' },
                fontWeight: 800,
                color: '#0f172a',
                lineHeight: 1,
                mb: 0.6,
              }}
            >
              {step.value}
            </Typography>

            {/* Uppercase Small Label */}
            <Typography
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.63rem', md: '0.65rem' },
                fontWeight: 700,
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                lineHeight: 1.22,
                textAlign: 'center',
                maxWidth: 95,
              }}
            >
              {step.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 1 - Left: Calibration Trend (Grouped Bar Chart)
export const CalibrationTrendWidget = ({
  data,
  timeframe = "3months",
  onTimeframeChange,
}) => {
  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <span>Calibration Trend</span>
            <Tooltip
              title="Tracks overall inward progress: when an inward is created or calibrated, it remains in 'Pending' until all certificates for that inward are approved, upon which it moves to 'Completed'."
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: '#94a3b8',
                  cursor: 'pointer',
                  transition: 'color 150ms ease',
                  '&:hover': { color: '#475569' },
                }}
              />
            </Tooltip>
          </Box>
        }
        mb={1.2}
        action={
          <WidgetTimeframeSelect
            value={timeframe}
            onChange={onTimeframeChange}
            minWidth={120}
            options={[
              { value: '3months', label: 'Last 3 Months' },
              { value: '6months', label: 'Last 6 Months' },
              { value: 'year', label: 'This Year' },
            ]}
          />
        }
      />

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.2, mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Pending</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Rejected</Typography>
        </Box>
      </Box>

      {/* Grouped Bar Chart */}
      <Box sx={{ height: 185, width: '100%', mt: 'auto' }}>
        <GroupedBarGraph data={data} />
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 1 - Center: Certificate Status (Donut Chart with central total and legend)
export const CertificateStatusWidget = ({
  stats = { total: 0, approved: 0, pending: 0, rejected: 0, amendment: 0 },
  timeframe = "monthly",
  onTimeframeChange,
}) => {
  const total = Number(stats.total) || 0;
  const approved = Number(stats.approved) || 0;
  const pending = Number(stats.pending) || 0;
  const rejected = Number(stats.rejected) || 0;
  const amendment = Number(stats.amendment) || 0;

  const getPct = (val) => (total > 0 ? Math.round((val / total) * 100) : 0);

  const doughnutData = {
    labels: ['Approved', 'Pending', 'Rejected', 'Amendment'],
    datasets: [
      {
        data: total > 0 ? [approved, pending, rejected, amendment] : [1],
        backgroundColor: total > 0
          ? ['#22c55e', '#f59e0b', '#ef4444', '#a855f7']
          : ['#e2e8f0'],
        borderWidth: 0,
        hoverOffset: 3,
      },
    ],
  };

  const statusItems = [
    { label: 'Approved', count: approved, pct: getPct(approved), color: '#22c55e' },
    { label: 'Pending', count: pending, pct: getPct(pending), color: '#f59e0b' },
    { label: 'Rejected', count: rejected, pct: getPct(rejected), color: '#ef4444' },
    { label: 'Amendment', count: amendment, pct: getPct(amendment), color: '#a855f7' },
  ];

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader
        title="Certificate Status"
        action={<WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />}
      />

      {/* Donut Chart & Legend */}
      <Grid container spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
        {/* Left Donut */}
        <Grid item xs={12} sm={5} md={5}>
          <Box sx={{ position: 'relative', width: 140, height: 140, mx: 'auto' }}>
            <DoughnutGraph data={doughnutData} cutout="70%" />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography sx={{ fontSize: '1.55rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                {total}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', mt: 0.3 }}>
                Total
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right Status Breakdown */}
        <Grid item xs={12} sm={7} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, pl: { md: 1 } }}>
            {statusItems.map((item, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: item.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
                    {item.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>
                  {item.count} <span style={{ fontWeight: 500, color: '#64748b', fontSize: '0.72rem' }}>({item.pct}%)</span>
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </DashboardWidgetCard>
  );
};

// Row 1 - Right: Discipline Wise Calibration (Horizontal bars)
export const DisciplineWiseWidget = ({
  disciplines = [],
  timeframe = "monthly",
  onTimeframeChange,
}) => {
  const items = disciplines || [];
  const maxCount = Math.max(...items.map((i) => i.count || 0), 1);

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader
        title="Discipline Wise Calibration"
        action={<WidgetTimeframeSelect value={timeframe} onChange={onTimeframeChange} />}
      />

      {/* Progress Bars List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1, justifyContent: items.length === 0 ? 'center' : 'space-around' }}>
        {items.length === 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center', py: 4 }}>
            No discipline calibration data found
          </Typography>
        ) : (
          items.map((item, idx) => {
            const widthPct = Math.max(Math.round(((item.count || 0) / maxCount) * 100), item.count > 0 ? 8 : 0);
            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '4px 6px',
                  borderRadius: '8px',
                  transition: 'background-color 150ms ease',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <Tooltip title={item.name} arrow placement="top">
                  <Typography
                    sx={{
                      width: { xs: 120, sm: 145, md: 155 },
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: '#334155',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'default',
                    }}
                  >
                    {item.name}
                  </Typography>
                </Tooltip>

                <Box sx={{ flex: 1, height: 8, borderRadius: '4px', bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${widthPct}%`,
                      height: '100%',
                      borderRadius: '4px',
                      bgcolor: item.color || '#0284c7',
                      transition: 'width 0.4s ease',
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    minWidth: 26,
                    textAlign: 'right',
                    fontSize: '0.82rem',
                    fontWeight: 750,
                    color: item.count > 0 ? '#0f172a' : '#94a3b8',
                    flexShrink: 0,
                  }}
                >
                  {item.count || 0}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </DashboardWidgetCard>
  );
};



// Row 2 - Right: Today's Activity (Vertical List)
export const TodayActivityWidget = ({
  counts = {
    inwards: 0,
    datasheets: 0,
    pendingApproval: 0,
    approved: 0,
  },
  hasAccessToModule,
}) => {
  const activities = [
    { moduleId: 3, label: 'Inwards Created', value: counts.inwards, icon: DownloadIcon, color: '#2563eb', bg: 'rgba(37, 99, 235, 0.08)', link: '/inward' },
    { moduleId: 4, label: 'Datasheets Completed', value: counts.datasheets, icon: ArticleIcon, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)', link: '/datasheet' },
    { moduleId: 5, label: 'Pending for Approval', value: counts.pendingApproval, icon: WatchLaterIcon, color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)', isRed: true, link: '/certificate' },
    { moduleId: 5, label: 'Approved Certificates', value: counts.approved, icon: CheckCircleIcon, color: '#16a34a', bg: 'rgba(22, 163, 74, 0.08)', link: '/certificate' },
  ].filter(act => !act.moduleId || (hasAccessToModule ? hasAccessToModule(act.moduleId) : true));

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <WidgetHeader title="Today's Activity" mb={1.8} />

      {/* Activity List - Clean transparent rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, flexGrow: 1, justifyContent: 'space-around' }}>
        {activities.map((act, idx) => {
          const IconComp = act.icon;
          return (
            <Link key={idx} to={act.link || "#"} style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: '7px',
                  px: '8px',
                  borderRadius: '8px',
                  transition: 'background-color 150ms ease',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
                  <IconComp sx={{ color: act.color, fontSize: 19 }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 550, color: '#334155' }}>
                    {act.label}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.92rem',
                    fontWeight: 750,
                    color: act.isRed ? '#dc2626' : '#0f172a',
                    minWidth: 24,
                    textAlign: 'right',
                  }}
                >
                  {act.value || 0}
                </Typography>
              </Box>
            </Link>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 3 - Left: Recent Inwards Table (Latest 5)
export const RecentInwardsWidget = ({ inwards = [] }) => {
  const rows = inwards || [];

  const getStatusStyle = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('complete') || s === '1') {
      return { color: '#16a34a', dot: '#16a34a', label: 'Completed' };
    }
    if (s.includes('pending') || s === '0') {
      return { color: '#d97706', dot: '#d97706', label: 'Pending' };
    }
    return { color: '#2563eb', dot: '#2563eb', label: 'In Progress' };
  };

  return (
    <DashboardWidgetCard sx={{ p: { xs: '14px 16px', sm: '16px 18px' } }}>
      {/* Header */}
      <WidgetHeader
        title="Recent Inwards"
        titleSx={{ fontSize: '0.88rem' }}
        mb={1}
        action={
          <Link to="/inward" style={{ textDecoration: 'none' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
              View All
            </Typography>
          </Link>
        }
      />

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto', border: 'none' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& th, & td': {
              border: 'none !important',
              borderBottom: '1px solid #f1f5f9 !important',
              py: '7px !important',
              px: '6px !important',
            },
          }}
        >
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important', pl: '10px !important' }}>Inward No</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important' }}>Client</TableCell>
              <TableCell align="center" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important' }}>Inst.</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important' }}>Date</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important', pr: '10px !important' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#94a3b8', fontSize: '0.78rem' }}>
                  No recent inwards found
                </TableCell>
              </TableRow>
            ) : (
              rows.slice(0, 5).map((row, index) => {
              const status = getStatusStyle(row.status);
              return (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    '&:last-child td': { borderBottom: 'none !important' },
                  }}
                >
                  <TableCell sx={{ pl: '10px !important', maxWidth: 110 }}>
                    <Tooltip title={row.inwardNo} arrow placement="top">
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 650, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.inwardNo}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 100 }}>
                    <Tooltip title={row.client} arrow placement="top">
                      <Typography sx={{ fontSize: '0.72rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.client}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#334155' }}>
                    {row.totalInstruments}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {row.date}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: '10px !important' }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: status.dot }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: status.color, whiteSpace: 'nowrap' }}>
                        {status.label}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            }))}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardWidgetCard>
  );
};

// Row 3 - Middle: Upcoming Due Dates Table (Latest 5)
export const UpcomingDueDatesWidget = ({ dueDates = [] }) => {
  const rows = dueDates || [];

  const getUrgencyColor = (days) => {
    const d = Number(days);
    if (d <= 5) return '#dc2626';
    if (d <= 10) return '#ea580c';
    return '#16a34a';
  };

  return (
    <DashboardWidgetCard sx={{ p: { xs: '14px 16px', sm: '16px 18px' } }}>
      {/* Header */}
      <WidgetHeader
        title="Upcoming Due Dates"
        titleSx={{ fontSize: '0.88rem' }}
        mb={1}
        action={
          <Link to="/scheduler" style={{ textDecoration: 'none' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
              View All
            </Typography>
          </Link>
        }
      />

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto', border: 'none' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& th, & td': {
              border: 'none !important',
              borderBottom: '1px solid #f1f5f9 !important',
              py: '7px !important',
              px: '6px !important',
            },
          }}
        >
          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
            <TableRow>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important', pl: '10px !important' }}>
                Request Name
              </TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important' }}>
                ID No
              </TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important' }}>
                Serial No
              </TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important' }}>
                Due Date
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', py: '8px !important', pr: '10px !important' }}>
                Days Left
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#94a3b8', fontSize: '0.78rem' }}>
                  No upcoming due dates found
                </TableCell>
              </TableRow>
            ) : (
              rows.slice(0, 5).map((row, index) => {
                const uColor = getUrgencyColor(row.daysLeft);
                return (
                  <TableRow
                    key={index}
                    hover
                    sx={{
                      '&:hover': { bgcolor: '#f8fafc' },
                      '&:last-child td': { borderBottom: 'none !important' },
                    }}
                  >
                    <TableCell sx={{ pl: '10px !important', maxWidth: 120 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 650, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.requestName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 85 }}>
                      <Typography sx={{ fontSize: '0.71rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.idNo || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 85 }}>
                      <Typography sx={{ fontSize: '0.71rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.serialNo || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {row.dueDate}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: '10px !important' }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: uColor, whiteSpace: 'nowrap' }}>
                        {row.daysLeft}d left
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardWidgetCard>
  );
};



